import React, { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { compressImageToDataURL } from '../../lib/imageUpload';

/**
 * A small button that opens the device file picker, compresses the chosen
 * image, and calls onUploaded(dataUrl). Hidden <input type="file"> internally.
 */
export const ImageUploadButton = ({
  onUploaded,
  label = 'Upload',
  size = 'sm',
  className = '',
  variant = 'secondary',
  'data-testid': testId,
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setBusy(true);
    try {
      const dataUrl = await compressImageToDataURL(file);
      onUploaded(dataUrl);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={handleClick}
        disabled={busy}
        className={`bg-purple-700 hover:bg-purple-600 text-white border-purple-500 ${className}`}
        data-testid={testId}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <Upload className="w-4 h-4 mr-1" />
        )}
        {label}
      </Button>
    </>
  );
};

export default ImageUploadButton;
